import api from "./api";
import { withActingInstitute } from "../utils/actingInstituteParam";

/**
 * Guardian Faculty Member (GFM) service — one method per backend endpoint.
 *
 * All calls go through the shared axios instance (`services/api.js`), whose
 * `baseURL` already includes `/api/v1`, so paths here are prefixed with `/gfm`
 * to match the backend router mounted at `/api/v1/gfm` (routes/v1/gfmRoutes.js).
 *
 * Callers are responsible for try/catch + user-facing error handling (toasts).
 */

// ─── Super Admin acting-institute context ───────────────────────────────────
// A Super Admin who picks an institute from the dropdown keeps their SA token
// (no operatingInstituteId in the token), so every GFM request must carry the
// acting institute id or the backend resolver (resolveActingInstituteContext)
// throws "Missing or unresolvable institute context" (401).
//
// Delegates to the shared `withActingInstitute` helper, which appends the
// selected institute as an `instituteId` QUERY param (the first source the
// backend SA fallback reads: req.query.instituteId). A query param is used —
// not a custom header — so no backend CORS change is needed; it works for
// GET/POST/PATCH/DELETE alike and is a no-op for non-SA roles (they resolve
// from req.user first). The shared helper PRESERVES an instituteId a caller
// already set (e.g. GfmSettings passing an explicit { instituteId }) and never
// overwrites it, and is fully guarded against malformed localStorage.
const withInstituteContext = (config = {}) => withActingInstitute(config);

// ─── Assignments (gfm.manage) ───

export const listAssignments = (params) =>
  api.get("/gfm/assignments", withInstituteContext({ params }));

export const createAssignment = (body) =>
  api.post("/gfm/assignments", body, withInstituteContext());

export const getAssignment = (id) =>
  api.get(`/gfm/assignments/${id}`, withInstituteContext());

export const updateAssignment = (id, body) =>
  api.patch(`/gfm/assignments/${id}`, body, withInstituteContext());

export const deleteAssignment = (id) =>
  api.delete(`/gfm/assignments/${id}`, withInstituteContext());

// ─── Mentees + unassigned discovery (gfm.manage) ───

export const assignMentees = (assignmentId, studentIds) =>
  api.post(
    `/gfm/assignments/${assignmentId}/mentees`,
    { studentIds },
    withInstituteContext()
  );

export const unassignMentee = (menteeId) =>
  api.delete(`/gfm/mentees/${menteeId}`, withInstituteContext());

export const listUnassignedStudents = (params) =>
  api.get("/gfm/students/unassigned", withInstituteContext({ params }));

// Institute classes for the mentee dialog's Class + Batch dropdowns. Optional
// `{ departmentId }` scopes the list to a department. Acting-institute context
// is threaded automatically for Super Admin.
export const listGfmClasses = (params = {}) =>
  api.get("/gfm/classes", withInstituteContext({ params }));

// ─── Institute configuration (gfm.manage) ───

// Optional `params` (e.g. { instituteId }) threads Super Admin institute
// context through as a query param for the dropdown-select flow. An explicitly
// provided instituteId is preserved (withInstituteContext never overwrites it).
// Backward compatible: no args → {} → no query param → unchanged behavior.
export const getConfig = (params = {}) =>
  api.get("/gfm/config", withInstituteContext({ params }));

export const updateConfig = (body, params = {}) =>
  api.patch("/gfm/config", body, withInstituteContext({ params }));

// ─── Reports + live defaulters (gfm.report.generate / gfm.mentees.view|view_all) ───

export const generateReport = (assignmentId, body) =>
  api.post(
    `/gfm/assignments/${assignmentId}/reports`,
    body,
    withInstituteContext()
  );

export const listReports = (params) =>
  api.get("/gfm/reports", withInstituteContext({ params }));

export const getReport = (reportId) =>
  api.get(`/gfm/reports/${reportId}`, withInstituteContext());

// PDF download — backend streams `application/pdf`; fetch as a blob so callers
// can trigger a browser download / object URL.
export const downloadReportPdf = (reportId) =>
  api.get(
    `/gfm/reports/${reportId}/pdf`,
    withInstituteContext({ responseType: "blob" })
  );

export const getDefaulters = (params) =>
  api.get("/gfm/defaulters", withInstituteContext({ params }));

// ─── Parent notifications (gfm.notify) ───

export const notifyReport = (reportId, body) =>
  api.post(
    `/gfm/reports/${reportId}/notify`,
    body,
    withInstituteContext()
  );

export const notifySingle = (reportId, studentId, body) =>
  api.post(
    `/gfm/reports/${reportId}/notify/${studentId}`,
    body,
    withInstituteContext()
  );

// Read the parent-notification delivery status (latest email + WhatsApp attempt
// per student) for a report. Response data:
//   { reportId, byStudent: [{ studentId, email:{status,reason,recipient,at}|null,
//                             whatsapp:{status,reason,recipient,at}|null }] }
export const getReportNotificationStatus = (reportId) =>
  api.get(`/gfm/reports/${reportId}/notifications`, withInstituteContext());
